// src/writer/hooks/useElementSelectionPreferences.ts
import { useState, useEffect } from 'react';
import { ProjectState } from '../types/project';
import { FrontMatter, BackMatter, AuthorDetails } from '../types';

interface ElementSelectionPreferences {
  frontMatter: {
    [key in keyof Pick<FrontMatter, 'titlePageContent' | 'copyrightPageContent' | 'dedication' | 'epigraph' | 'tableOfContents' | 'foreword' | 'forewordBy' | 'preface' | 'acknowledgmentsFront' | 'prologue' | 'introduction' | 'titleImagePrompt'>]?: boolean;
  };
  backMatter: {
    [key in keyof Pick<BackMatter, 'epilogue' | 'afterword' | 'authorsNote' | 'acknowledgmentsBack' | 'aboutTheAuthor' | 'readingGroupGuide' | 'teaserNextBook' | 'index' | 'backCoverImagePrompt'>]?: boolean;
  };
  authorDetails: {
    [key in keyof Pick<AuthorDetails, 'isbn' | 'publisher' | 'edition' | 'copyrightNotice'>]?: boolean;
  };
  lastUpdated: string;
}

const STORAGE_KEY = 'storyLikho_elementSelectionPreferences';

// Default selections based on fiction vs non-fiction
const getDefaultSelections = (projectState?: ProjectState): ElementSelectionPreferences => ({
  frontMatter: {
    dedication: true,
    prologue: projectState?.bookMetadata?.fictionVsNonFiction === 'Fiction',
    introduction: projectState?.bookMetadata?.fictionVsNonFiction === 'Non-fiction',
    titlePageContent: false,
    copyrightPageContent: false,
    epigraph: false,
    tableOfContents: false,
    foreword: false,
    forewordBy: false,
    preface: false,
    acknowledgmentsFront: false,
    titleImagePrompt: false,
  },
  backMatter: {
    epilogue: projectState?.bookMetadata?.fictionVsNonFiction === 'Fiction',
    afterword: false,
    authorsNote: true,
    acknowledgmentsBack: true,
    aboutTheAuthor: true,
    readingGroupGuide: false,
    teaserNextBook: false,
    index: false,
    backCoverImagePrompt: false,
  },
  authorDetails: {
    isbn: false,
    publisher: false,
    edition: false,
    copyrightNotice: false,
  },
  lastUpdated: new Date().toISOString()
});

export const useElementSelectionPreferences = (projectState?: ProjectState) => {
  const [preferences, setPreferences] = useState<ElementSelectionPreferences>(() => {
    // Try to load from localStorage on initialization
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ElementSelectionPreferences;

        // Migration: Remove imageElements if present (backward compatibility)
        if ((parsed as any).imageElements) {
          delete (parsed as any).imageElements;
        }

        // Migration: Add new image description fields if missing
        let needsUpdate = false;
        if (parsed.frontMatter && !parsed.frontMatter.hasOwnProperty('titleImagePrompt')) {
          parsed.frontMatter.titleImagePrompt = false;
          needsUpdate = true;
        }
        if (parsed.backMatter && !parsed.backMatter.hasOwnProperty('backCoverImagePrompt')) {
          parsed.backMatter.backCoverImagePrompt = false;
          needsUpdate = true;
        }

        // If we added new fields, save the updated preferences immediately
        if (needsUpdate) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        }

        // Check if preferences are recent (within 30 days)
        const lastUpdated = new Date(parsed.lastUpdated);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        if (lastUpdated > thirtyDaysAgo) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Failed to load element selection preferences from localStorage:', error);
    }

    // Return defaults if no valid stored preferences
    return getDefaultSelections(projectState);
  });

  // Save preferences to localStorage whenever they change
  const savePreferences = (newPreferences: ElementSelectionPreferences) => {
    try {
      const preferencesToSave = {
        ...newPreferences,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferencesToSave));
      setPreferences(preferencesToSave);
    } catch (error) {
      console.error('Failed to save element selection preferences to localStorage:', error);
      // Still update state even if localStorage fails
      setPreferences(newPreferences);
    }
  };

  // Update specific category selections
  const updateCategoryPreferences = (
    category: keyof Omit<ElementSelectionPreferences, 'lastUpdated'>,
    categoryPreferences: any
  ) => {
    const newPreferences = {
      ...preferences,
      [category]: categoryPreferences
    };
    savePreferences(newPreferences);
  };

  // Update individual element preference
  const updateElementPreference = (
    category: keyof Omit<ElementSelectionPreferences, 'lastUpdated'>,
    elementKey: string,
    selected: boolean
  ) => {
    const newPreferences = {
      ...preferences,
      [category]: {
        ...preferences[category],
        [elementKey]: selected
      }
    };
    savePreferences(newPreferences);
  };

  // Reset to defaults
  const resetToDefaults = () => {
    const defaults = getDefaultSelections(projectState);
    savePreferences(defaults);
  };

  // Get selected elements as array
  const getSelectedElements = (): string[] => {
    const selectedElements: string[] = [];

    // Collect selected front matter elements
    Object.entries(preferences.frontMatter).forEach(([key, selected]) => {
      if (selected) {
        selectedElements.push(key);
      }
    });

    // Collect selected back matter elements
    Object.entries(preferences.backMatter).forEach(([key, selected]) => {
      if (selected) {
        selectedElements.push(key);
      }
    });

    // Collect selected author details elements
    Object.entries(preferences.authorDetails).forEach(([key, selected]) => {
      if (selected) {
        selectedElements.push(key);
      }
    });

    return selectedElements;
  };

  // Get count of selected elements
  const getSelectedCount = (): number => {
    let count = 0;
    Object.values(preferences.frontMatter).forEach(selected => selected && count++);
    Object.values(preferences.backMatter).forEach(selected => selected && count++);
    Object.values(preferences.authorDetails).forEach(selected => selected && count++);
    return count;
  };

  // Update defaults when project state changes (e.g., fiction vs non-fiction)
  useEffect(() => {
    if (projectState?.bookMetadata?.fictionVsNonFiction) {
      const currentDefaults = getDefaultSelections(projectState);

      // Only update prologue/introduction based on fiction type if they haven't been explicitly set
      const needsUpdate =
        (projectState.bookMetadata.fictionVsNonFiction === 'Fiction' &&
         preferences.frontMatter.introduction && !preferences.frontMatter.prologue) ||
        (projectState.bookMetadata.fictionVsNonFiction === 'Non-fiction' &&
         preferences.frontMatter.prologue && !preferences.frontMatter.introduction);

      if (needsUpdate) {
        const updatedPreferences = {
          ...preferences,
          frontMatter: {
            ...preferences.frontMatter,
            prologue: currentDefaults.frontMatter.prologue,
            introduction: currentDefaults.frontMatter.introduction,
          },
          backMatter: {
            ...preferences.backMatter,
            epilogue: currentDefaults.backMatter.epilogue,
          }
        };
        savePreferences(updatedPreferences);
      }
    }
  }, [projectState?.bookMetadata?.fictionVsNonFiction]);

  return {
    preferences,
    savePreferences,
    updateCategoryPreferences,
    updateElementPreference,
    resetToDefaults,
    getSelectedElements,
    getSelectedCount
  };
};